import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MetricsCard from './MetricsCard';

const TestimonialsPanel = () => {
  const [testimonials, setTestimonials] = useState([
    {
      id: 1,
      customerName: 'Priya & Arjun',
      customerEmail: 'priya.arjun@example.com',
      vendorName: 'Rajesh Photography Studio',
      rating: 5,
      content: 'Amazing photography service! Rajesh captured every precious moment of our wedding beautifully. Highly recommended!',
      status: 'approved',
      featured: true,
      createdAt: '2025-07-10T14:30:00Z',
      weddingDate: '2025-07-05T00:00:00Z'
    },
    {
      id: 2,
      customerName: 'Sneha & Karan',
      customerEmail: 'sneha.karan@example.com',
      vendorName: 'Dream Decorators',
      rating: 4,
      content: 'Beautiful decoration setup for our engagement. The team was professional and creative. Some minor delays but overall great experience.',
      status: 'pending',
      featured: false,
      createdAt: '2025-07-12T09:15:00Z',
      weddingDate: '2025-07-08T00:00:00Z'
    },
    {
      id: 3,
      customerName: 'Ravi & Meera',
      customerEmail: 'ravi.meera@example.com',
      vendorName: 'Spice Route Catering',
      rating: 5,
      content: 'Exceptional catering service! The food was delicious and the presentation was outstanding. All our guests loved it!',
      status: 'approved',
      featured: false,
      createdAt: '2025-07-11T16:45:00Z',
      weddingDate: '2025-07-06T00:00:00Z'
    }
  ]);

  const [reviews, setReviews] = useState([
    {
      id: 1,
      customerName: 'Anita Sharma',
      vendorName: 'Elite Mehndi Artists',
      rating: 5,
      content: 'Intricate and beautiful mehndi designs. The artist was skilled and patient.',
      status: 'approved',
      createdAt: '2025-07-12T11:20:00Z'
    },
    {
      id: 2,
      customerName: 'Rohit Kumar',
      vendorName: 'City Banquet Hall',
      rating: 3,
      content: 'Average venue with basic facilities. Service could be improved.',
      status: 'flagged',
      createdAt: '2025-07-12T08:30:00Z'
    }
  ]);

  const [activeTab, setActiveTab] = useState('testimonials');

  const testimonialStats = {
    totalTestimonials: testimonials.length,
    pending: testimonials.filter(t => t.status === 'pending').length,
    featured: testimonials.filter(t => t.featured).length,
    avgRating: testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length
  };

  const reviewStats = {
    totalReviews: reviews.length,
    flagged: reviews.filter(r => r.status === 'flagged').length,
    approved: reviews.filter(r => r.status === 'approved').length,
    avgRating: reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'flagged': return 'text-red-600 bg-red-100';
      case 'rejected': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={16}
        className={i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
      />
    ));
  };

  const handleStatusUpdate = (id, newStatus, type) => {
    if (type === 'testimonial') {
      setTestimonials(prev => prev.map(testimonial => 
        testimonial.id === id 
          ? { ...testimonial, status: newStatus }
          : testimonial
      ));
    } else {
      setReviews(prev => prev.map(review => 
        review.id === id 
          ? { ...review, status: newStatus }
          : review
      ));
    }
  };

  const handleToggleFeatured = (id) => {
    setTestimonials(prev => prev.map(testimonial => 
      testimonial.id === id 
        ? { ...testimonial, featured: !testimonial.featured }
        : testimonial
    ));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2">
          Testimonials & Reviews Manager
        </h2>
        <p className="text-yellow-100">
          Manage customer testimonials and vendor reviews
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {activeTab === 'testimonials' ? (
          <>
            <MetricsCard
              title="Total Testimonials"
              value={testimonialStats.totalTestimonials}
              icon="MessageSquare"
              color="blue"
            />
            <MetricsCard
              title="Pending Review"
              value={testimonialStats.pending}
              icon="Clock"
              color="orange"
            />
            <MetricsCard
              title="Featured"
              value={testimonialStats.featured}
              icon="Star"
              color="yellow"
            />
            <MetricsCard
              title="Avg Rating"
              value={testimonialStats.avgRating.toFixed(1)}
              icon="ThumbsUp"
              color="green"
            />
          </>
        ) : (
          <>
            <MetricsCard
              title="Total Reviews"
              value={reviewStats.totalReviews}
              icon="MessageCircle"
              color="blue"
            />
            <MetricsCard
              title="Flagged"
              value={reviewStats.flagged}
              icon="Flag"
              color="red"
            />
            <MetricsCard
              title="Approved"
              value={reviewStats.approved}
              icon="CheckCircle"
              color="green"
            />
            <MetricsCard
              title="Avg Rating"
              value={reviewStats.avgRating.toFixed(1)}
              icon="Star"
              color="purple"
            />
          </>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-soft">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('testimonials')}
            className={`px-6 py-4 font-medium ${
              activeTab === 'testimonials' ?'text-primary border-b-2 border-primary' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            Testimonials
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-6 py-4 font-medium ${
              activeTab === 'reviews' ?'text-primary border-b-2 border-primary' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            Reviews
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'testimonials' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-xl text-text-primary">
                  Customer Testimonials
                </h3>
                <Button variant="primary">
                  <Icon name="Plus" size={16} className="mr-2" />
                  Add Testimonial
                </Button>
              </div>

              <div className="space-y-4">
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="font-medium text-text-primary">{testimonial.customerName}</h4>
                          <div className="flex items-center space-x-1">
                            {renderStars(testimonial.rating)}
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(testimonial.status)}`}>
                            {testimonial.status}
                          </span>
                          {testimonial.featured && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                              Featured
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          Vendor: {testimonial.vendorName}
                        </p>
                        <p className="text-text-secondary mb-3">{testimonial.content}</p>
                        <div className="flex items-center space-x-4 text-sm text-text-muted">
                          <span>Wedding: {formatDate(testimonial.weddingDate)}</span>
                          <span>Submitted: {formatDate(testimonial.createdAt)}</span>
                        </div>
                      </div>
                      <div className="flex flex-col space-y-2 ml-4">
                        {testimonial.status === 'pending' && (
                          <>
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={() => handleStatusUpdate(testimonial.id, 'approved', 'testimonial')}
                            >
                              Approve
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleStatusUpdate(testimonial.id, 'rejected', 'testimonial')}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {testimonial.status === 'approved' && (
                          <Button 
                            variant={testimonial.featured ? 'outline' : 'primary'} 
                            size="sm"
                            onClick={() => handleToggleFeatured(testimonial.id)}
                          >
                            <Icon name="Star" size={16} className="mr-1" />
                            {testimonial.featured ? 'Unfeature' : 'Feature'}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm">
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-xl text-text-primary">
                  Vendor Reviews
                </h3>
              </div>

              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="font-medium text-text-primary">{review.customerName}</h4>
                          <div className="flex items-center space-x-1">
                            {renderStars(review.rating)}
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(review.status)}`}>
                            {review.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          Vendor: {review.vendorName}
                        </p>
                        <p className="text-text-secondary mb-3">{review.content}</p>
                        <p className="text-sm text-text-muted">
                          Submitted: {formatDate(review.createdAt)}
                        </p>
                      </div>
                      <div className="flex flex-col space-y-2 ml-4">
                        {review.status === 'flagged' && (
                          <>
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={() => handleStatusUpdate(review.id, 'approved', 'review')}
                            >
                              Approve
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleStatusUpdate(review.id, 'rejected', 'review')}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="sm">
                          <Icon name="Flag" size={16} />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Icon name="MessageSquare" size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestimonialsPanel;